import React from 'react'
import Nav from '../../src/components/Nav'
import { urls } from '../../src/utils/config'
import Footer from '../../src/components/Footer'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles } from '@material-ui/core/styles'
import Background from '../../src/components/Subnav/Background'
import CourseGrid from '../../src/components/Academy/Course/CourseGrid'
import { Typography, Divider } from '@material-ui/core'
import HelpSection from '../../src/components/Help/HelpSection'
import JoinCommunitySection from '../../src/components/Footer/JoinCommunitySection'

import { getCourses } from '../../src/services/academy'
import { Course as ICourse } from '../../types/academy'
import { GetServerSideProps } from 'next'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '1200px',
    margin: 'auto',
  },
  aboveFold: {
    minHeight: '20vh',
  },
  contentSection: {
    margin: '40px 0',
    padding: '20px',
    [theme.breakpoints.up('md')]: {
      margin: '120px 0',
    },
    [theme.breakpoints.up('lg')]: {
      margin: '160px 0',
    },
  },
  title: {
    color: 'white',
    textAlign: 'left',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
      maxWidth: '100%',
      margin: '0 auto 0 auto',
    },
  },
  subtitle: {
    marginTop: '20px',
    marginBottom: '30px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '16rem',
  },
  heroTextSection: {
    width: '100%',
    padding: '20px',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
  section: {},
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '80px 0',
  },
}))

interface Props {
  courses: ICourse[]
}

function Courses({ courses }: Props): JSX.Element {
  const classes = useStyles({})
  const beginnerCourses = courses.filter((course) => course.level === 'beginner')
  const intermediateCourses = courses.filter((course) => course.level === 'intermediate')
  const proCourses = courses.filter((course) => course.level === 'expert' || course.level === 'pro')

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.home,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters | The #1 Crypto Product Comparison Platform`}
            description={`Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`}
            openGraph={{
              url: `https://${process.env.NEXT_PUBLIC_APP_URL}/courses`,
              title: `Cryptotesters | The #1 Crypto Product Comparison Platform`,
              description: `Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/courses.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/courses.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <div className={classes.aboveFold}>
              <Background size="large" />
              <div className={classes.heroContent}>
                <div className={classes.heroTextSection}>
                  <Typography variant="h1" className={classes.title}>
                    Browse Courses
                  </Typography>
                  <Typography variant="subtitle2" className={classes.subtitle}>
                    Look at our wide selection of courses to get you from Crypto Beginner to
                    Professional in no time.
                  </Typography>
                </div>
              </div>
            </div>
            <div className={classes.contentSection}>
              {courses.length > 0 && (
                <div>
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Featured Courses </Typography>
                  </div>
                  <CourseGrid courses={courses} type="details" />
                </div>
              )}

              {beginnerCourses.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Beginner Courses </Typography>
                  </div>
                  <CourseGrid courses={beginnerCourses} type="details" />
                </div>
              )}

              {intermediateCourses.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Intermediate Courses </Typography>
                  </div>
                  <CourseGrid courses={intermediateCourses} type="details" />
                </div>
              )}

              {proCourses.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Pro Courses </Typography>
                  </div>
                  <CourseGrid courses={proCourses} type="details" />
                </div>
              )}
            </div>
          </div>
          <HelpSection />
          <JoinCommunitySection />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let courses: ICourse[] = []

  try {
    courses = await getCourses()
  } catch (e) {
    console.error(e.message)
  }

  return {
    props: {
      courses,
    },
  }
}

export default Courses
